// The dashboard's two languages: Japanese by default, English on request.
//
// The English sentence is its own key (house rule 5: a lookup table). A
// component writes `t('Last updated {when}', { when })`; in English that is the
// sentence, in Japanese it is looked up in locales/ja.js. A sentence missing
// from the table falls back to English, never to a key, and
// test/ui/i18n.test.js lists every `t('…')` in the app and fails if Japanese
// lacks one. Decision 0030.
//
// Chosen per browser and kept there (`bw-dashboard-lang`). `?lang=en` or
// `?lang=ja` in a link sets it, so a link can open in the language it was
// sent in. Slack messages, and the previews that show them, stay English: the
// channel reads English.
import { ref } from 'vue'
import JA from '../locales/ja.js'

export const LANGS = ['ja', 'en']
export const DEFAULT_LANG = 'ja'
const KEY = 'bw-dashboard-lang'

function stored() {
  try {
    const asked = new URLSearchParams(window.location.search).get('lang')
    if (LANGS.includes(asked)) {
      localStorage.setItem(KEY, asked)
      return asked
    }
  } catch {
    /* no window, or no storage: the default */
  }
  try {
    const saved = localStorage.getItem(KEY)
    if (LANGS.includes(saved)) return saved
  } catch {
    /* blocked storage: the default */
  }
  return DEFAULT_LANG
}

/** The language on screen. Read inside `t`, so whatever renders with it follows a change. */
export const lang = ref(typeof window === 'undefined' ? DEFAULT_LANG : stored())

/** Switch language, remember it, and tell the document (screen readers, fonts, hyphenation). */
export function setLang(next) {
  if (!LANGS.includes(next)) return
  lang.value = next
  try {
    localStorage.setItem(KEY, next)
  } catch {
    /* kept for this visit only */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = next
}

/**
 * Set while a Slack message is being built: the channel reads English, and the
 * message builders share helpers with the screen (decision 0030).
 */
let forced = null

/** Run `build` with every `t` answering in English. Returns what it returns. */
export function inEnglish(build) {
  const was = forced
  forced = 'en'
  try {
    return build()
  } finally {
    forced = was
  }
}

const current = () => forced ?? lang.value

/** `{name}` in a sentence, filled from `params`; a missing one stays visible rather than blank. */
const fill = (text, params) =>
  params
    ? text.replace(/\{(\w+)\}/g, (all, k) =>
        k in params && params[k] != null ? String(params[k]) : all,
      )
    : text

/** The sentence in the language on screen. */
export function t(text, params) {
  return fill(current() === 'ja' ? (JA[text] ?? text) : text, params)
}

/**
 * Marks a sentence for translation where it is defined, in a table read at run
 * time (`{ good: tk('Good') }`); `t` translates it where it is shown. Returns
 * the text unchanged. It is there so test/ui/i18n.test.js can find the sentence.
 */
export const tk = (text) => text

/**
 * `t` for a count: `one` when `n` is 1, `many` otherwise, with `{n}` filled.
 * Both are literal sentences, so test/ui/i18n.test.js can find them.
 */
export const tn = (n, one, many, params) => t(n === 1 ? one : many, { n, ...params })

/** True while Japanese is on screen: for the few formats that are not sentences (dates). */
export const isJa = () => current() === 'ja'
